import React, { useState, useEffect, useMemo } from 'react'
import { supabase } from './supabaseClient'
import { C } from './theme'
import { Landmark, AlertTriangle, Info } from 'lucide-react'

// IVA a entregar, por trimestre.
//
// Não substitui a declaração — essa faz-se no Sage com o contabilista. Serve
// para saber quanto reservar e quando, que é o que evita sustos.
//
// PORQUE FUNCIONA COM AS FACTURAS ISENTAS: soma-se o IVA REAL de cada mês
// (valor_com_iva − valor_sem_iva) em vez de aplicar 23% ao total. Uma factura
// isenta — indemnização de seguro, OSS, o que for — traz IVA zero e contribui
// zero, sem ninguém ter de a marcar. Calcular 23% sobre tudo inventaria imposto
// que não existe.

const fmt = (n) => Number(n || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const CHAVE_IVA_DEDUTIVEL = 'iva_dedutivel_mensal'

const TRIMESTRES = [
  { n: 1, nome: '1.º trimestre', meses: [1, 2, 3], label: 'Jan–Mar' },
  { n: 2, nome: '2.º trimestre', meses: [4, 5, 6], label: 'Abr–Jun' },
  { n: 3, nome: '3.º trimestre', meses: [7, 8, 9], label: 'Jul–Set' },
  { n: 4, nome: '4.º trimestre', meses: [10, 11, 12], label: 'Out–Dez' },
]

export default function IvaTrimestral({ declarada, ano, sempreAberto = false }) {
  const [despesas, setDespesas] = useState('')
  const [guardado, setGuardado] = useState(false)
  // Valor REAL apurado de cada trimestre. Quando existe, manda ele — e serve
  // para calibrar a estimativa dos trimestres seguintes.
  const [reais, setReais] = useState({})
  // IVA cobrado a consumidores de OUTROS Estados-Membros (Kaufland ES, 21%) e
  // declarado no OSS. O mapa de vendas do Sage traz este IVA somado ao
  // nacional — nao os separa — por isso tem de sair daqui: nao e imposto a
  // entregar a Portugal, vai para o Estado do comprador, noutra guia e noutra
  // data. Sem isto o ecra dizia 8.817,92 € no T3/2026 quando a Portugal eram
  // 8.568,90 €. Fica por trimestre na config: se em 2027 o OSS acabar, a chave
  // fica vazia e volta a ser tudo nacional sem mexer em codigo.
  const [oss, setOss] = useState({})
  // A guia do OSS paga-se um mes antes da nacional, por isso tem marca propria.
  // Sem ela o valor ficava na tesouraria como saida em atraso para sempre — o
  // mesmo erro que o iva_pago_ ja existia para evitar no IVA portugues.
  const [ossPagos, setOssPagos] = useState({})
  // Trimestres ja entregues. Sem isto o total soma o que ja saiu da conta em
  // Maio com o que ainda ha-de sair, e o numero que aparece nao serve para nada.
  const [pagos, setPagos] = useState({})
  // Fechado quando vive dentro de outra pagina; aberto quando E a pagina.
  const [aberto, setAberto] = useState(sempreAberto)
  const [erroCfg, setErroCfg] = useState('')

  useEffect(() => {
    supabase.from('config_sistema').select('chave, valor')
      .or(`chave.eq.${CHAVE_IVA_DEDUTIVEL},chave.like.iva_real_%,chave.like.iva_pago_%,chave.like.iva_oss_%`)
      .then(({ data, error }) => {
        // Sem isto, uma leitura bloqueada apagava do ecra os valores reais e as
        // marcas de pago sem dizer nada: tudo voltava a estimativa e a por
        // pagar, e o total do ano passava a somar o que ja saiu da conta.
        if (error) { setErroCfg('Não foi possível ler a configuração do IVA: ' + error.message); return }
        setErroCfg('')
        const m = {}, pg = {}, os = {}, osPg = {}
        for (const r of (data || [])) {
          if (r.chave === CHAVE_IVA_DEDUTIVEL) { if (r.valor != null) setDespesas(String(r.valor)) }
          // A verificacao do pago vem ANTES da numerica: "1" tambem e um numero
          // e iria parar aos valores reais.
          else if (r.chave.startsWith('iva_pago_')) { if (r.valor === '1') pg[r.chave] = true }
          // iva_oss_pago_ ANTES de iva_oss_, senao o prefixo mais curto apanha-o
          // primeiro e o "1" da marca de pago entrava como se fosse 1 € de OSS.
          else if (r.chave.startsWith('iva_oss_pago_')) { if (r.valor === '1') osPg[r.chave] = true }
          // E o OSS vem ANTES do apanha-tudo, pela mesma razao: senao os
          // 249,02 € do OSS entravam como valor real do trimestre e passavam a
          // mandar sobre a estimativa.
          else if (r.chave.startsWith('iva_oss_')) { if (r.valor != null) os[r.chave] = Number(r.valor) }
          else if (r.valor != null) m[r.chave] = Number(r.valor)
        }
        setReais(m); setPagos(pg); setOss(os); setOssPagos(osPg)
      })
  }, [])

  const chaveReal = (t) => `iva_real_${ano}_T${t}`
  const gravarReal = async (t, valor) => {
    const k = chaveReal(t)
    setReais(p => ({ ...p, [k]: valor === '' ? undefined : Number(valor) }))
    await supabase.from('config_sistema')
      .upsert({ chave: k, valor: valor === '' ? null : String(valor) }, { onConflict: 'chave' })
  }

  const gravarOss = async (t, valor) => {
    const k = `iva_oss_${ano}_T${t}`
    setOss(p => ({ ...p, [k]: valor === '' ? undefined : Number(valor) }))
    await supabase.from('config_sistema')
      .upsert({ chave: k, valor: valor === '' ? null : String(valor) }, { onConflict: 'chave' })
  }

  const gravarOssPago = async (t, pago) => {
    const k = `iva_oss_pago_${ano}_T${t}`
    setOssPagos(p => ({ ...p, [k]: pago }))
    await supabase.from('config_sistema')
      .upsert({ chave: k, valor: pago ? '1' : null }, { onConflict: 'chave' })
  }

  const gravarPago = async (t, pago) => {
    const k = `iva_pago_${ano}_T${t}`
    setPagos(p => ({ ...p, [k]: pago }))
    await supabase.from('config_sistema')
      .upsert({ chave: k, valor: pago ? '1' : null }, { onConflict: 'chave' })
  }

  const guardar = async () => {
    await supabase.from('config_sistema')
      .upsert({ chave: CHAVE_IVA_DEDUTIVEL, valor: despesas === '' ? null : String(despesas) }, { onConflict: 'chave' })
    setGuardado(true)
    setTimeout(() => setGuardado(false), 2000)
  }

  // Lança-se o IVA dedutível directamente, não o valor da despesa. É o que se
  // sabe de cor, e é mais fiável do que uma conversão: as despesas têm taxas
  // diferentes e nem todas são integralmente dedutíveis.
  const dedutivelMes = Number(despesas) || 0

  const trimestres = useMemo(() => {
    const doAno = declarada.filter(d => d.ano === Number(ano))
    const ivaPorMes = new Map(doAno.map(d => [
      d.mes,
      Math.round(((Number(d.valor_com_iva || 0) - Number(d.valor_sem_iva || 0))) * 100) / 100,
    ]))
    const hoje = new Date()
    // O ANO TEM DE ENTRAR NA CONTA. Comparava-se o mes do trimestre com o mes de
    // hoje sem olhar ao ano, portanto um trimestre de 2025 visto em Agosto de
    // 2026 dizia que ainda nao tinha fechado, e um ano futuro apareceria todo
    // fechado antes de existir.
    const anoActual = hoje.getFullYear()
    const mesActual = Number(ano) < anoActual ? 13
                    : Number(ano) > anoActual ? 0
                    : hoje.getMonth() + 1

    return TRIMESTRES.map(t => {
      const comDados = t.meses.filter(m => ivaPorMes.has(m))
      const liquidado = comDados.reduce((s, m) => s + (ivaPorMes.get(m) || 0), 0)
      // Só se abate a despesa dos meses que já se conhecem
      const dedutivel = dedutivelMes * comDados.length
      const fechado = t.meses.every(m => m < mesActual)
      const real = reais[`iva_real_${ano}_T${t.n}`]
      const temReal = real != null && Number.isFinite(real)
      // O OSS sai ANTES de tudo o resto: do IVA liquidado no mapa do Sage, uma
      // parte nem sequer e de Portugal.
      const ossT = Number(oss[`iva_oss_${ano}_T${t.n}`]) || 0
      // Com valor real, a diferenca para o liquidado E a deducao efectiva —
      // melhor medida do que qualquer media escrita a mao. Tira-se o OSS, senao
      // a deducao aparecia inflacionada pelo imposto que foi para outro Estado.
      const dedutivelReal = temReal ? Math.round((liquidado - ossT - real) * 100) / 100 : null
      return {
        ...t,
        nMeses: comDados.length,
        completo: comDados.length === 3,
        fechado,
        liquidado: Math.round(liquidado * 100) / 100,
        oss: Math.round(ossT * 100) / 100,
        // O OSS abate-se ao nacional esteja pago ou nao — nunca foi imposto
        // portugues. A marca so diz a tesouraria que o dinheiro ja saiu.
        ossPago: !!ossPagos[`iva_oss_pago_${ano}_T${t.n}`],
        dedutivel: Math.round(dedutivel * 100) / 100,
        aEntregar: Math.round((liquidado - ossT - dedutivel) * 100) / 100,
        real: temReal ? real : null,
        temReal,
        dedutivelReal,
        dedutivelRealMes: temReal && comDados.length > 0
          ? Math.round((dedutivelReal / comDados.length) * 100) / 100 : null,
        pago: !!pagos[`iva_pago_${ano}_T${t.n}`],
      }
    }).filter(t => t.nMeses > 0)
  }, [declarada, ano, dedutivelMes, reais, pagos, oss, ossPagos])

  // Real onde ja se sabe, estimado no resto — a regra e a mesma em todo o lado.
  const valorDe = (t) => t.temReal ? t.real : t.aEntregar
  const totalAno = trimestres.reduce((s, t) => s + valorDe(t), 0)
  const totalPago = trimestres.filter(t => t.pago).reduce((s, t) => s + valorDe(t), 0)
  const porPagar = trimestres.filter(t => !t.pago).reduce((s, t) => s + valorDe(t), 0)
  const haPagos = trimestres.some(t => t.pago)
  // Marcado como pago mas sem valor real: o total esta a usar a estimativa de
  // uma coisa que ja se sabe ao certo. Vale a pena dize-lo.
  const pagosSemReal = trimestres.filter(t => t.pago && !t.temReal)
  const totLiquidado = trimestres.reduce((s, t) => s + t.liquidado, 0)
  const totOss = trimestres.reduce((s, t) => s + t.oss, 0)
  const totDedutivel = trimestres.reduce((s, t) => s + t.dedutivel, 0)
  const totEstimado = trimestres.reduce((s, t) => s + t.aEntregar, 0)
  const totReal = trimestres.filter(t => t.temReal).reduce((s, t) => s + t.real, 0)
  const nComReal = trimestres.filter(t => t.temReal).length
  // A calibragem: o trimestre mais recente com valor real diz a deducao efectiva
  const calibragem = [...trimestres].reverse().find(t => t.temReal && t.dedutivelRealMes != null)
  const pesoDespesas = trimestres.reduce((s, t) => s + t.liquidado, 0)

  if (trimestres.length === 0) return null

  const avisoCfg = erroCfg ? (
    <div style={{ margin: '0 0 10px', padding: '9px 12px', background: '#fde7e9', borderLeft: '4px solid #a4262c', borderRadius: 3, color: '#a4262c', fontSize: 12, fontWeight: 600 }}>
      <AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
      {erroCfg} — os valores reais e as marcas de pago não estão a ser lidos.
    </div>
  ) : null

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '14px', overflow: 'hidden' }}>
      {avisoCfg && <div style={{ padding: '10px 16px 0' }}>{avisoCfg}</div>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', padding: '12px 16px', background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <button onClick={() => setAberto(a => !a)}
          style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '13px', fontWeight: '800', color: C.text }}>
          <span style={{ color: C.textMuted, fontSize: '11px' }}>{aberto ? '▾' : '▸'}</span>
          <Landmark size={15} color={C.primary} />
          IVA a entregar {ano}
          <span style={{ color: C.primary, fontWeight: '800' }}>{fmt(haPagos ? porPagar : totalAno)} €</span>
          <span style={{ fontSize: '11px', fontWeight: '600', color: C.textMuted }}>
            {haPagos ? `· por pagar (${fmt(totalPago)} € já entregues)` : '· até agora'}
          </span>
        </button>
        {aberto && <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: C.text, fontWeight: '600' }}>
          IVA dedutível médio/mês
          <input type="number" min="0" step="10" value={despesas}
            onChange={e => setDespesas(e.target.value)} onBlur={guardar}
            placeholder="0" title="Valor do IVA que deduzes por mês, já apurado — não o total das despesas."
            style={{ width: '80px', padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', textAlign: 'right' }} />
          €
          {guardado && <span style={{ color: C.success, fontSize: '11px', fontWeight: '700' }}>✓</span>}
        </label>}
      </div>

      {!aberto ? null : (<>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
        <thead>
          <tr style={{ background: C.surfaceAlt }}>
            <th style={th}>Trimestre</th>
            <th style={{ ...th, textAlign: 'right' }}>IVA liquidado</th>
            <th style={{ ...th, textAlign: 'right' }} title="IVA cobrado a clientes de outros países da UE. Vem somado no mapa do Sage, mas não é de Portugal.">OSS (outros Estados)</th>
            <th style={{ ...th, textAlign: 'right' }}>Dedutível (despesas)</th>
            <th style={{ ...th, textAlign: 'right' }}>Estimado</th>
            <th style={{ ...th, textAlign: 'right' }}>Valor real</th>
            <th style={{ ...th, textAlign: 'center' }}>Entregue</th>
          </tr>
        </thead>
        <tbody>
          {trimestres.map(t => (
            <tr key={t.n} style={{ borderTop: `1px solid ${C.border}`, background: t.pago ? C.successSoft : 'transparent' }}>
              <td style={{ padding: '8px 16px', color: C.text }}>
                <strong>{t.nome}</strong> <span style={{ color: C.textMuted, fontSize: '11px' }}>{t.label}</span>
                {!t.completo && (
                  <span title="O trimestre ainda não está completo — faltam meses por facturar ou por importar"
                    style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#92400e', background: '#fff4ce', border: '1px solid #c0740055', borderRadius: 8, padding: '1px 6px' }}>
                    {t.nMeses} de 3 meses
                  </span>
                )}
              </td>
              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '700', color: C.text }}>{fmt(t.liquidado)} €</td>
              <td style={{ padding: '6px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                <input type="number" min="0" step="0.01"
                  value={t.oss > 0 ? t.oss : ''}
                  onChange={e => gravarOss(t.n, e.target.value)}
                  placeholder="—"
                  title="IVA do documento de pagamento da declaração do OSS deste trimestre. Vem somado no mapa do Sage, mas vai para o Estado do comprador — abate aqui e paga-se à parte, noutra data."
                  style={{ width: '92px', padding: '4px 7px', textAlign: 'right', border: `1px solid ${t.oss > 0 ? '#c07400' : C.border}`, borderRadius: '3px', fontSize: '12.5px', fontWeight: t.oss > 0 ? '800' : '400', color: t.oss > 0 ? '#92400e' : C.text }} />
                {t.oss > 0 && (
                  <label title="Marca quando a guia do OSS já tiver saído da conta. Sai da projecção da tesouraria — mas continua a abater ao IVA nacional, porque nunca foi imposto de Portugal."
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginLeft: '6px', cursor: 'pointer', fontSize: '10px', fontWeight: '800', color: t.ossPago ? C.success : C.textMuted }}>
                    <input type="checkbox" checked={t.ossPago} onChange={e => gravarOssPago(t.n, e.target.checked)} style={{ cursor: 'pointer' }} />
                    {t.ossPago ? 'pago' : ''}
                  </label>
                )}
              </td>
              <td style={{ padding: '8px 16px', textAlign: 'right', color: C.textMuted }}>
                {t.dedutivel > 0 ? '−' + fmt(t.dedutivel) + ' €' : '—'}
              </td>
              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: t.temReal ? '400' : '800', color: t.temReal ? C.textMuted : C.primary, fontSize: t.temReal ? '12px' : '13px', textDecoration: t.temReal ? 'line-through' : 'none' }}>
                {fmt(t.aEntregar)} €
              </td>
              <td style={{ padding: '6px 16px', textAlign: 'right' }}>
                <input type="number" min="0" step="0.01"
                  value={t.temReal ? t.real : ''}
                  onChange={e => gravarReal(t.n, e.target.value)}
                  placeholder="—"
                  title="Valor apurado a sério, quando já o souberes. Manda sobre a estimativa e calibra os trimestres seguintes."
                  style={{ width: '96px', padding: '4px 7px', textAlign: 'right', border: `1px solid ${t.temReal ? C.success : C.border}`, borderRadius: '3px', fontSize: '12.5px', fontWeight: t.temReal ? '800' : '400', color: t.temReal ? C.success : C.text }} />
              </td>
              <td style={{ padding: '6px 16px', textAlign: 'center' }}>
                <label title="Marca quando o pagamento ao Estado já tiver saído. Sai do total por pagar."
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '800', color: t.pago ? C.success : C.textMuted }}>
                  <input type="checkbox" checked={t.pago} onChange={e => gravarPago(t.n, e.target.checked)} style={{ cursor: 'pointer' }} />
                  {t.pago ? 'Pago' : ''}
                </label>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: `2px solid ${C.border}`, background: C.surfaceAlt }}>
            <td style={{ padding: '9px 16px', fontWeight: '800', color: C.text, fontSize: '12.5px' }}>
              Total {ano}
              <span style={{ fontWeight: '400', color: C.textMuted, fontSize: '11px' }}>
                {' '}· {trimestres.length} trimestre(s) com dados
              </span>
            </td>
            <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: '800', color: C.text }}>{fmt(totLiquidado)} €</td>
            <td style={{ padding: '9px 16px', textAlign: 'right', color: '#92400e', fontWeight: '700' }}>
              {totOss > 0 ? '−' + fmt(totOss) + ' €' : '—'}
            </td>
            <td style={{ padding: '9px 16px', textAlign: 'right', color: C.textMuted, fontWeight: '700' }}>
              {totDedutivel > 0 ? '−' + fmt(totDedutivel) + ' €' : '—'}
            </td>
            <td style={{ padding: '9px 16px', textAlign: 'right', color: C.textMuted, fontWeight: '700' }}>{fmt(totEstimado)} €</td>
            <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: '800', color: C.success }}>
              {nComReal > 0 ? fmt(totReal) + ' €' : '—'}
            </td>
            <td></td>
          </tr>
          {/* Dois numeros diferentes e ambos verdadeiros: o do ano inteiro serve
              para a contabilidade, o que falta pagar serve para a conta bancaria.
              Juntos num so induziam em erro — o 1.o trimestre ja saiu em Maio. */}
          <tr style={{ borderTop: `1px solid ${C.border}`, background: C.bg }}>
            <td colSpan="6" style={{ padding: haPagos ? '8px 16px' : '10px 16px', fontWeight: '800', color: haPagos ? C.textMuted : C.text, fontSize: haPagos ? '12px' : '13px' }}>
              {haPagos ? 'Total do ano' : `A ENTREGAR EM ${ano}`}
              <span style={{ fontWeight: '400', color: C.textMuted, fontSize: '11px' }}>
                {' '}· valor real onde já se sabe, estimado no resto
              </span>
            </td>
            <td style={{ padding: haPagos ? '8px 16px' : '10px 16px', textAlign: 'right', fontWeight: '800', color: haPagos ? C.textMuted : C.primary, fontSize: haPagos ? '13px' : '15px', whiteSpace: 'nowrap' }}>{fmt(totalAno)} €</td>
          </tr>
          {haPagos && (
            <tr style={{ borderTop: `1px solid ${C.border}`, background: C.bg }}>
              <td colSpan="6" style={{ padding: '10px 16px', fontWeight: '800', color: C.text, fontSize: '13px' }}>
                AINDA POR PAGAR
                <span style={{ fontWeight: '400', color: C.textMuted, fontSize: '11px' }}>
                  {' '}· já entregaste {fmt(totalPago)} € em {trimestres.filter(t => t.pago).map(t => t.nome.replace(' trimestre', '.º T')).join(', ')}
                </span>
              </td>
              <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '800', color: C.primary, fontSize: '15px', whiteSpace: 'nowrap' }}>{fmt(porPagar)} €</td>
            </tr>
          )}
        </tfoot>
      </table>

      {/* A explicação que evita a expectativa errada sobre as despesas */}
      <div style={{ padding: '11px 16px', borderTop: `1px solid ${C.border}`, fontSize: '11.5px', color: C.textMuted, lineHeight: 1.65 }}>
        {pagosSemReal.length > 0 && (
          <div style={{ marginBottom: '6px', padding: '7px 9px', background: '#fff4ce', border: '1px solid #c0740055', borderRadius: '3px', color: '#92400e' }}>
            <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            <strong>{pagosSemReal.map(t => t.nome).join(' e ')}</strong> {pagosSemReal.length > 1 ? 'estão marcados' : 'está marcado'} como
            {pagosSemReal.length > 1 ? ' pagos' : ' pago'} mas sem valor real. O total está a contar
            {pagosSemReal.length > 1 ? ' as estimativas' : ' a estimativa'} de uma coisa que já sabes ao certo —
            mete o valor entregue na coluna <em>Valor real</em> e o ano fecha certo.
          </div>
        )}
        {calibragem && (
          <div style={{ marginBottom: '6px', padding: '7px 9px', background: C.successSoft, border: `1px solid ${C.success}44`, borderRadius: '3px', color: C.success }}>
            <strong>Calibrado pelo {calibragem.nome}:</strong> liquidaste {fmt(calibragem.liquidado)} € e pagas {fmt(calibragem.real)} €,
            o que significa <strong>{fmt(calibragem.dedutivelRealMes)} € de dedução por mês</strong> — medido, não estimado.
            {Math.abs(calibragem.dedutivelRealMes - dedutivelMes) > 1 && (
              <> Tens {fmt(dedutivelMes)} € no campo acima; vale a pena passar para {fmt(calibragem.dedutivelRealMes)} €.</>
            )}
          </div>
        )}
        {dedutivelMes > 0 && pesoDespesas > 0 && (
          <div style={{ marginBottom: '6px' }}>
            <Info size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            <strong style={{ color: C.text }}>{fmt(dedutivelMes)} € de IVA dedutível por mês</strong> abatem{' '}
            <strong style={{ color: C.text }}>{(trimestres.reduce((s, t) => s + t.dedutivel, 0) / pesoDespesas * 100).toFixed(1)}%</strong> do que há a entregar.
          </div>
        )}
        <strong style={{ color: C.text }}>Porque não abate mais:</strong> as compras ao fornecedor são intracomunitárias
        com autoliquidação — IVA 0%, nada para deduzir. Só abatem as despesas correntes. Num negócio que comprasse
        cá, o IVA das compras absorveria a maior parte do que há a entregar.
        <br />
        <strong style={{ color: C.text }}>Facturas isentas contam zero automaticamente</strong> — soma-se o IVA real
        de cada mês e não 23% do total, por isso indemnizações de seguro e outras isenções não inventam imposto.
        <br />
        <strong style={{ color: C.text }}>O OSS é o contrário: não é isenção, tem de se tirar à mão.</strong> As vendas
        a consumidores de outros países da UE levam IVA do país deles (Espanha, 21%) e o mapa do Sage traz esse imposto
        <strong style={{ color: C.text }}> somado ao nacional</strong> — não os separa. Esse IVA não é de Portugal: vai
        para o Estado do comprador, por guia própria e com prazo próprio (fim do mês a seguir ao trimestre, antes do
        IVA nacional). Mete na coluna <em>OSS</em> o valor do documento de pagamento que o contabilista enviar. Sem isso,
        o ecrã pede-te para reservar imposto a mais e a tesouraria projecta uma saída só, quando são duas.
        <br />
        <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4, color: '#92400e' }} />
        Isto serve para <strong style={{ color: C.text }}>reservar dinheiro</strong>, não para declarar. A declaração
        faz-se no Sage com o contabilista — em especial a autoliquidação das compras, que tem tratamento próprio.
      </div>
      </>)}
    </div>
  )
}

const th = { padding: '7px 16px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.4px' }
